// frontend/src/utils/thermalLogo.js
//
// The browser mirror of backend/app/services/logo_raster.py.
//
// The cropper promises a shop owner two things: how many millimetres of every
// receipt their logo will eat, and what it will look like once the printer has
// thrown away every shade of grey. Both promises are only worth something if
// the arithmetic here is the same arithmetic the printer path uses, so this
// module reproduces it step for step and its tests carry the same numbers as
// test_logo_raster.py.
//
// ONE known inexactness: canvas resampling is not LANCZOS, so a pixel sitting
// on a hard edge can land on the other side of the threshold. The preview is
// faithful in shape, tone and paper cost; it is not bit-identical.

// 384 dots is the raster width used for the logo on both emitter paths.
export const MAX_WIDTH_DOTS = 384;

// 96 dots is 12.0mm at 203dpi — Aqana's own 1608x400 logo scaled to 384 wide.
// Roughly four lines of body text. See logo_raster.MAX_HEIGHT for why it is a
// height ceiling and not an aspect lock.
export const MAX_HEIGHT_DOTS = 96;

// The ceiling turned on its side. Fitted to 96 dots tall, a portrait logo is
// left a few millimetres wide — a smear rather than a mark.
export const MIN_FITTED_WIDTH_DOTS = 96;

// 203 dots per inch is the density of every thermal head in the field.
export const DOTS_PER_MM = 203 / 25.4;

/** Printer dots as millimetres of paper. */
export function dotsToMm(dots) {
    return dots / DOTS_PER_MM;
}

/**
 * The largest size inside the box that keeps the aspect ratio.
 *
 * Never enlarges — upscaling a small logo to head width produces a blurry mess
 * that thresholds into noise. Mirrors logo_raster.fit_within exactly, including
 * the clamp, so a crop the cropper accepts is a crop the server accepts.
 */
export function fitWithin(width, height, maxWidth, maxHeight) {
    if (!(width > 0) || !(height > 0)) return { width: 1, height: 1 };
    const scale = Math.min(maxWidth / width, maxHeight / height, 1);
    return {
        width: Math.min(maxWidth, Math.max(1, Math.round(width * scale))),
        height: Math.min(maxHeight, Math.max(1, Math.round(height * scale))),
    };
}

/**
 * A translation key naming why this crop cannot be uploaded, or null.
 *
 * A key rather than a sentence: the message reaches a tenant through t(), and
 * an English string returned from here would be untranslatable. The rule is the
 * server's rule (logo_raster.validate_logo_bytes), checked here first so the
 * tenant is told before they wait for an upload to fail.
 */
export function logoRejection(fittedWidth) {
    if (fittedWidth < MIN_FITTED_WIDTH_DOTS) return 'logo_crop_too_narrow';
    return null;
}

/**
 * Everything the cropper needs to say about a crop, in one place.
 *
 * Millimetres, not dots and not "text lines": a shop owner can picture 12mm of
 * paper. `rejection` is the hard block — the same rule the server applies, run
 * here first so the tenant is told before they wait for an upload to fail.
 */
export function describeCrop(cropWidth, cropHeight) {
    const fitted = fitWithin(cropWidth, cropHeight, MAX_WIDTH_DOTS, MAX_HEIGHT_DOTS);
    return {
        fitted,
        widthMm: dotsToMm(fitted.width),
        heightMm: dotsToMm(fitted.height),
        rejection: logoRejection(fitted.width),
    };
}

/**
 * ITU-R 601-2 luma — the formula Pillow's convert("L") uses.
 *
 * Not a coincidence and not interchangeable with the other common weightings:
 * a different set shifts every pixel near the cut, which is precisely the set
 * of pixels the preview exists to show.
 *
 * Measured against Pillow's own convert("L") over 140,608 colours: 356 differ
 * by one level, because Pillow works in 16-bit fixed point and this works in
 * decimal, and 3 of those 356 land on the other side of the 128 threshold.
 */
export function luminance(r, g, b) {
    return Math.round((r * 299 + g * 587 + b * 114) / 1000);
}

/**
 * Hard-threshold an ImageData in place at 128, no dithering.
 *
 * A logo is line art: solid shapes with hard edges and no grey to reproduce.
 * Dithering shatters those edges into a stipple to fake a tone that was never
 * there — on paper it reads as a smudge rather than a mark. `level > 128` is
 * white, matching logo_raster's `point()` exactly, so 128 itself fires the dot.
 */
export function thresholdImageData(imageData) {
    const { data } = imageData;
    for (let i = 0; i < data.length; i += 4) {
        const value = luminance(data[i], data[i + 1], data[i + 2]) > 128 ? 255 : 0;
        data[i] = value;
        data[i + 1] = value;
        data[i + 2] = value;
        data[i + 3] = 255;
    }
    return imageData;
}

/**
 * A 2d context, or null.
 *
 * Null is a real outcome, not only a test artefact: a browser refuses a context
 * once too many canvases are live, and jsdom has none at all. The caller draws
 * nothing rather than throwing — a missing preview is a cosmetic problem, an
 * exception in a settings page is a page the tenant cannot use.
 */
function context2d(canvas) {
    try {
        return (canvas && canvas.getContext && canvas.getContext('2d')) || null;
    } catch (error) {
        return null;
    }
}

/**
 * Draw a source region onto a canvas as the printer would see it.
 *
 * Composite onto white FIRST — a logo with an alpha channel otherwise converts
 * to near-black and prints as a solid rectangle that eats the paper — then
 * scale, then threshold. Same order as raster_from_bytes.
 *
 * Returns the canvas, or null if the browser gave us no 2d context (jsdom).
 */
export function renderThermal(canvas, source, crop, fitted) {
    const ctx = context2d(canvas);
    if (!ctx) return null;

    canvas.width = fitted.width;
    canvas.height = fitted.height;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, fitted.width, fitted.height);
    ctx.drawImage(
        source,
        crop.x, crop.y, crop.width, crop.height,
        0, 0, fitted.width, fitted.height,
    );

    const imageData = ctx.getImageData(0, 0, fitted.width, fitted.height);
    thresholdImageData(imageData);
    ctx.putImageData(imageData, 0, 0);
    return canvas;
}

/**
 * The crop as a PNG blob, at up to 4x the print box.
 *
 * Not at 384x96: the same file is the portal's brand logo, and 384x96 looks
 * poor in a browser sidebar. The renderer downsamples at print time with
 * LANCZOS, which is better than anything canvas would have produced anyway.
 * Still bounded, so a 12-megapixel phone photo does not become a 2MB upload.
 */
export const EXPORT_SCALE = 4;

export function exportCrop(source, crop) {
    const canvas = document.createElement('canvas');
    const ctx = context2d(canvas);
    if (!ctx) return Promise.resolve(null);

    const size = fitWithin(
        crop.width, crop.height,
        MAX_WIDTH_DOTS * EXPORT_SCALE, MAX_HEIGHT_DOTS * EXPORT_SCALE,
    );
    canvas.width = size.width;
    canvas.height = size.height;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size.width, size.height);
    ctx.drawImage(
        source,
        crop.x, crop.y, crop.width, crop.height,
        0, 0, size.width, size.height,
    );

    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
}
